import React, { useEffect, useState } from "react";
import { PartyPopper, Sparkles, GraduationCap, Briefcase } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { api } from "@/lib/api";

const fmtMin = (m) => {
  const h = Math.floor(m / 60); const r = m % 60;
  return h ? `${h}h ${r ? r + "m" : ""}`.trim() : `${r || 0}m`;
};

function CompanyGoalCard({ testId, icon: Icon, label, unit, goal }) {
  if (!goal) return null;
  const pct = goal.target ? Math.min(100, Math.round((100 * goal.value) / goal.target)) : 0;
  const achieved = pct >= 100;
  const remaining = Math.max(0, Math.round((goal.target - goal.value) * 10) / 10);
  return (
    <div data-testid={testId} className="border-l-4 border-emerald-500 bg-emerald-50/40 p-4 flex-1 min-w-[220px]">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-overline text-slate-500">
        <Icon size={12} strokeWidth={1.5} /> {label} · dieser Monat
      </div>
      {/* Positiv zuerst: was bereits erreicht wurde, groß & grün */}
      <div className="font-heading text-3xl font-light tracking-tighter text-emerald-700 mt-1" data-testid={`${testId}-value`}>
        {goal.value} <span className="text-emerald-500 text-base font-normal">{unit}</span>
        <span className="text-slate-400 text-sm font-normal"> geschafft</span>
      </div>
      {/* Thermometer-Füllstand */}
      <div className="h-2.5 bg-white border border-emerald-200 mt-2 overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-slate-600 mt-1.5">
        {achieved ? (
          <span className="text-emerald-700 font-medium">Monatsziel von {goal.target} {unit} erreicht — stark! 🎉</span>
        ) : (
          <>Noch <span className="text-slate-900 font-medium">{remaining} {unit}</span> bis zum Ziel von {goal.target} {unit}</>
        )}
      </div>
    </div>
  );
}

/** Team-wide celebration widget — combined pie of everyone's logged time this
 * week + weekly-goal completion. No individual comparisons, visible to all staff. */
export default function TeamCelebrationWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/kpi/team-celebration").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const pieData = data.by_category || [];
  const goalsPct = data.goals_total ? Math.round((100 * data.goals_done) / data.goals_total) : 0;
  const cg = data.company_goals;

  return (
    <div className="border border-slate-200 bg-white" data-testid="team-celebration-widget-inner">
      <div className="p-5 border-b border-slate-200 flex items-baseline gap-2">
        <PartyPopper size={15} strokeWidth={1.5} className="text-[#00807E]" />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Team-Erfolg · diese Woche</div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Was das Team diese Woche geschafft hat</div>
        </div>
      </div>
      {cg && (
        <div className="p-5 pb-0 flex flex-wrap gap-4" data-testid="company-goals-row">
          <CompanyGoalCard testId="company-goal-coaching-ue" icon={Briefcase} label="Coaching-UEs" unit="UE"
            goal={cg.coaching_ue} />
          <CompanyGoalCard testId="company-goal-wb-participants" icon={GraduationCap} label="Neue WB-Teilnehmer" unit="Stk"
            goal={cg.wb_participants} />
        </div>
      )}
      <div className="p-5 grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-6 items-center">
        {pieData.length === 0 ? (
          <div className="text-sm text-slate-400 italic col-span-full text-center py-6">Diese Woche wurde noch keine Zeit erfasst.</div>
        ) : (
          <>
            <div style={{ minWidth: 160, minHeight: 160 }}>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={pieData} innerRadius={44} outerRadius={72} paddingAngle={2} dataKey="minutes" stroke="none">
                    {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtMin(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="space-y-1 text-xs" data-testid="team-celebration-legend">
              {pieData.map((d) => (
                <li key={d.category} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 shrink-0" style={{ background: d.color }} />
                  <span className="flex-1 text-slate-800">{d.name}</span>
                  <span className="font-mono text-slate-500">{fmtMin(d.minutes)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="flex md:flex-col gap-4 md:gap-3 md:border-l md:border-slate-100 md:pl-6 shrink-0">
          <div data-testid="team-celebration-hours">
            <div className="font-heading text-2xl font-light text-slate-900">{fmtMin(data.total_minutes)}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-overline">Team-Zeit erfasst</div>
          </div>
          <div data-testid="team-celebration-goals" className="flex items-center gap-1.5">
            <Sparkles size={13} strokeWidth={1.5} className="text-amber-500" />
            <div>
              <div className="font-heading text-2xl font-light text-slate-900">{goalsPct}%</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-overline">{data.goals_done}/{data.goals_total} Wochenziele erreicht</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
